import { Alert, Button, Chip, ChipProps, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { useSubscriptionsInfo } from '@/api/subscriptions';
import { Timestamp } from '@/components/data/Timestamp';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';

export const SubscriptionStatusSummaryBox = () => {
    const { data: subscriptionInfo, isPending: isSubscriptionInfoPending, isError, error } = useSubscriptionsInfo();
    const noSubscription =
        !isError && !isSubscriptionInfoPending && subscriptionInfo && !subscriptionInfo?.data?.length;
    const hasSubscription = !!subscriptionInfo?.data?.length;

    /*
        TODO (https://app.shortcut.com/sofar/story/241149/change-behavior-for-cancelling-subscription):
        Once we switch to cancel immediately, we can get rid of this.
     */
    const isAutoCancelationOn = !!subscriptionInfo?.data?.some(
        ({ cancel_at_period_end, cancel_at }) => cancel_at_period_end && cancel_at
    );

    return (
        <Stack sx={{ border: '1px solid', borderColor: 'divider', p: 3, gap: 2, alignItems: 'start' }}>
            <Typography variant="h5" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                Automatic subscription payments
                {(noSubscription || hasSubscription) && (
                    <SubscriptionStatusChip
                        sx={{ ml: 1 }}
                        active={hasSubscription}
                        willAutoCancel={isAutoCancelationOn}
                    />
                )}
            </Typography>
            {isSubscriptionInfoPending && <GenericSkeleton lines={2} />}
            {isError && (
                <Alert severity="error" data-testid="subscription-status-error">
                    Error loading subscription{error?.message && `: ${error?.message}`}
                </Alert>
            )}
            {noSubscription && (
                <>
                    <Typography variant="body2">
                        Enable device telemetry to sign up for a monthly recurring subscription and get automatically
                        charged each month.
                    </Typography>
                    <Button href={'/telemetry'} variant="contained">
                        Enable telemetry
                    </Button>
                </>
            )}
            {hasSubscription && (
                <>
                    {isAutoCancelationOn ? (
                        <Typography variant="body2">
                            Your subscription will be canceled after the current billing period ends on{' '}
                            <Timestamp value={subscriptionInfo.data[0].cancel_at} unit="date" />
                        </Typography>
                    ) : (
                        <Typography variant="body2">
                            You will be automatically charged each month at the end of each invoice period using the
                            payment method on file.
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        color="error"
                        sx={{ textWrap: 'nowrap', whiteSpace: 'nowrap' }}
                        href="/payment/cancel"
                        data-testid="cancel-subscription-open-dialog-button"
                    >
                        Cancel subscription
                    </Button>
                </>
            )}
        </Stack>
    );
};

const SubscriptionStatusChip = ({
    active,
    willAutoCancel,
    sx,
}: {
    active: boolean;
    willAutoCancel: boolean;
    sx: ChipProps['sx'];
}) => {
    const { label, color } = useMemo(() => {
        if (!active) {
            return { label: 'Inactive', color: 'default' } as const;
        }
        if (willAutoCancel) {
            return { label: 'Scheduled for cancellation', color: 'warning' } as const;
        }
        return { label: 'Active', color: 'success' } as const;
    }, [active, willAutoCancel]);

    return (
        <Chip
            {...{ sx }}
            label={<Typography variant="caption">{label}</Typography>}
            variant="filled"
            color={color}
            size="small"
        />
    );
};
